/**
 * Pure geometry and color utility functions extracted from shape components.
 * No MapLibre or DOM dependencies — fully testable in isolation.
 */

// ---------------------------------------------------------------------------
// Color utilities (from base-shape.ts)
// ---------------------------------------------------------------------------

/**
 * Determine whether a hex colour is perceptually "light" using the
 * ITU-R BT.601 luma formula: (R*299 + G*587 + B*114) / 1000 > 128.
 */
export function isLightColor(hex: string): boolean {
  const h = hex.replace('#', '')
  const r = parseInt(h.substring(0, 2), 16)
  const g = parseInt(h.substring(2, 4), 16)
  const b = parseInt(h.substring(4, 6), 16)
  return (r * 299 + g * 587 + b * 114) / 1000 > 128
}

// ---------------------------------------------------------------------------
// Rectangle utilities (from shape-rectangle.ts)
// ---------------------------------------------------------------------------

/**
 * Calculate the arithmetic-mean center of a set of corner coordinates.
 * Corners are [lng, lat] arrays.
 */
export function calculateRectangleCenter(corners: number[][]): [number, number] {
  const centerLng = corners.reduce((s, c) => s + c[0], 0) / corners.length
  const centerLat = corners.reduce((s, c) => s + c[1], 0) / corners.length
  return [centerLng, centerLat]
}

/**
 * Translate every corner by a lng/lat offset.
 * Returns a new array — does not mutate the input.
 */
export function offsetRectangleCorners(
  corners: number[][],
  offsetLng: number,
  offsetLat: number
): number[][] {
  return corners.map(c => [c[0] + offsetLng, c[1] + offsetLat])
}

/**
 * Close a polygon ring by appending the first coordinate at the end,
 * suitable for GeoJSON Polygon geometry.
 */
export function buildClosedRectanglePolygon(corners: number[][]): number[][][] {
  return [[...corners, corners[0]]]
}

// ---------------------------------------------------------------------------
// Line utilities (from shape-line.ts)
// ---------------------------------------------------------------------------

/**
 * Compute the midpoint between two [lng, lat] coordinates via simple averaging.
 */
export function calculateMidpoint(coordA: number[], coordB: number[]): [number, number] {
  return [(coordA[0] + coordB[0]) / 2, (coordA[1] + coordB[1]) / 2]
}

/**
 * Return a new coordinate array with the vertex at `vertexIndex` replaced.
 * Does not mutate the input array.
 */
export function updateVertexPosition(
  coords: number[][],
  vertexIndex: number,
  newLng: number,
  newLat: number
): number[][] {
  const result = coords.map(c => [...c])
  result[vertexIndex] = [newLng, newLat]
  return result
}

/**
 * Return a new coordinate array with a new vertex inserted after `afterIndex`.
 * Does not mutate the input array.
 */
export function insertMidpointVertex(
  coords: number[][],
  afterIndex: number,
  newLng: number,
  newLat: number
): number[][] {
  const result = coords.map(c => [...c])
  result.splice(afterIndex + 1, 0, [newLng, newLat])
  return result
}

// ---------------------------------------------------------------------------
// Polygon utilities (from shape-polygon.ts)
// ---------------------------------------------------------------------------

/**
 * Calculate the arithmetic-mean centroid (lng, lat) of a polygon vertex list.
 * Vertices are [lng, lat] arrays. The closing vertex (duplicate of first) must
 * already be stripped before calling — i.e., pass `coords.slice(0, -1)`.
 */
export function calculatePolygonCentroid(vertices: number[][]): [number, number] {
  const lng = vertices.reduce((s, c) => s + c[0], 0) / vertices.length
  const lat = vertices.reduce((s, c) => s + c[1], 0) / vertices.length
  return [lng, lat]
}

/**
 * Translate every vertex by a lng/lat offset.
 * Returns a new array — does not mutate the input.
 */
export function translatePolygonVertices(
  vertices: number[][],
  offsetLng: number,
  offsetLat: number
): number[][] {
  return vertices.map(c => [c[0] + offsetLng, c[1] + offsetLat])
}

// ---------------------------------------------------------------------------
// Line handle visibility (from shape-line.ts rebuildVisibleHandles)
// ---------------------------------------------------------------------------

/** A minimal viewport-bounds description (west/east/south/north in degrees). */
export interface ViewportBounds {
  west: number
  east: number
  south: number
  north: number
}

/** A screen-space point with x/y in pixels. */
export interface ScreenPoint {
  x: number
  y: number
}

/**
 * Filter a list of [lng, lat] coordinates to only those that are:
 *   1. Within the viewport bounds (plus `pad` degrees of padding), AND
 *   2. At least `minDistancePx` screen pixels from the previously kept vertex
 *      (first and last coordinates are always kept if in bounds).
 *
 * `project` converts a [lng, lat] pair to a screen-space `ScreenPoint`.
 * Returns an array of `{ index, px }` entries for the accepted vertices.
 */
export function filterVisibleVertices(
  coords: number[][],
  bounds: ViewportBounds,
  project: (lngLat: [number, number]) => ScreenPoint,
  minDistancePx: number,
  pad = 0.01
): Array<{ index: number; px: ScreenPoint }> {
  const result: Array<{ index: number; px: ScreenPoint }> = []
  let lastPx: ScreenPoint | null = null

  for (let i = 0; i < coords.length; i++) {
    const [lng, lat] = coords[i]

    if (
      lng < bounds.west - pad ||
      lng > bounds.east + pad ||
      lat < bounds.south - pad ||
      lat > bounds.north + pad
    ) {
      continue
    }

    const px = project([lng, lat])
    const isEndpoint = i === 0 || i === coords.length - 1

    if (!isEndpoint && lastPx) {
      const dx = px.x - lastPx.x
      const dy = px.y - lastPx.y
      if (dx * dx + dy * dy < minDistancePx * minDistancePx) {
        continue
      }
    }

    result.push({ index: i, px })
    lastPx = px
  }

  return result
}

/**
 * Return true if the screen-space distance between two points is at least
 * `minDistancePx` pixels — i.e., the segment is long enough to warrant a
 * midpoint handle.
 */
export function shouldShowMidpoint(
  pt1: ScreenPoint,
  pt2: ScreenPoint,
  minDistancePx: number
): boolean {
  const dx = pt2.x - pt1.x
  const dy = pt2.y - pt1.y
  return dx * dx + dy * dy >= minDistancePx * minDistancePx
}

// ---------------------------------------------------------------------------
// Line editing: delete, split, join
// ---------------------------------------------------------------------------

/** Known per-coordinate metadata array property names. */
const COORD_METADATA_KEYS = [
  'coordTimes',
  'coordAccuracies',
  'coordSpeeds',
  'coordActivities',
  'coordDemElevations',
] as const

/**
 * Delete vertices at the given indices from a coordinate array.
 * Returns null if fewer than 2 points would remain.
 */
export function deleteVerticesFromCoords(
  coords: number[][],
  indicesToDelete: Set<number>
): number[][] | null {
  const result = coords.filter((_, i) => !indicesToDelete.has(i))
  return result.length >= 2 ? result : null
}

/**
 * Remove entries at the given indices from all per-coordinate metadata arrays
 * in a properties object. Returns a new properties object.
 */
export function filterMetadataArrays(
  properties: Record<string, unknown>,
  indicesToDelete: Set<number>
): Record<string, unknown> {
  const result = { ...properties }
  for (const key of COORD_METADATA_KEYS) {
    const arr = result[key]
    if (Array.isArray(arr)) {
      result[key] = arr.filter((_, i) => !indicesToDelete.has(i))
    }
  }
  return result
}

/**
 * Split a LineString coordinate array at the given vertex index.
 * The split vertex is included in both halves.
 * Returns null if the index is 0 or the last index (endpoints can't split).
 */
export function splitLineAtVertex(
  coords: number[][],
  vertexIndex: number
): [number[][], number[][]] | null {
  if (vertexIndex <= 0 || vertexIndex >= coords.length - 1) return null
  const first = coords.slice(0, vertexIndex + 1)
  const second = coords.slice(vertexIndex)
  return [first, second]
}

/**
 * Split all per-coordinate metadata arrays at the given vertex index.
 * The split index is included in both halves (mirroring splitLineAtVertex).
 */
export function splitMetadataArrays(
  properties: Record<string, unknown>,
  vertexIndex: number
): [Record<string, unknown>, Record<string, unknown>] {
  const first: Record<string, unknown> = { ...properties }
  const second: Record<string, unknown> = { ...properties }
  for (const key of COORD_METADATA_KEYS) {
    const arr = properties[key]
    if (Array.isArray(arr)) {
      first[key] = arr.slice(0, vertexIndex + 1)
      second[key] = arr.slice(vertexIndex)
    }
  }
  return [first, second]
}

/**
 * Compute split segments from a coordinate array based on selected vertex indices.
 * Each contiguous group of selected/unselected vertices becomes a segment.
 * Boundary vertices (where selection status changes) are shared between adjacent segments.
 * Returns null if the result would be fewer than 2 segments.
 */
export function computeSplitSegments(
  coordCount: number,
  selectedIndices: Set<number>
): Array<[number, number]> | null {
  if (selectedIndices.size === 0) return null

  const sorted = [...selectedIndices].sort((a, b) => a - b)

  const cutPoints: number[] = []
  let groupStart = sorted[0]
  for (let i = 1; i <= sorted.length; i++) {
    if (i === sorted.length || sorted[i] !== sorted[i - 1] + 1) {
      if (groupStart > 0) cutPoints.push(groupStart)
      if (sorted[i - 1] < coordCount - 1) cutPoints.push(sorted[i - 1])
      if (i < sorted.length) groupStart = sorted[i]
    }
  }

  const unique = [...new Set(cutPoints)].sort((a, b) => a - b)
  if (unique.length === 0) return null

  const segments: Array<[number, number]> = []
  let start = 0
  for (const cut of unique) {
    if (cut > start) {
      segments.push([start, cut])
      start = cut
    }
  }
  if (start < coordCount - 1) {
    segments.push([start, coordCount - 1])
  }

  return segments.length >= 2 ? segments : null
}

/**
 * Slice metadata arrays for a set of split segments.
 * Each segment is defined by [startIndex, endIndex] (inclusive).
 * Returns one properties object per segment.
 */
export function sliceMetadataForSegments(
  properties: Record<string, unknown>,
  segments: Array<[number, number]>
): Record<string, unknown>[] {
  return segments.map(([start, end]) => {
    const result = { ...properties }
    for (const key of COORD_METADATA_KEYS) {
      const arr = result[key]
      if (Array.isArray(arr)) {
        result[key] = arr.slice(start, end + 1)
      }
    }
    return result
  })
}

/**
 * Merge two LineString coordinate arrays with explicit orientation.
 * Near-duplicate endpoints at the join point (within ~0.0001 deg) are deduplicated.
 * Returns the merged array and whether a duplicate was skipped.
 */
export function mergeLineCoordinates(
  coordsA: number[][],
  coordsB: number[][],
  reverseA: boolean,
  reverseB: boolean
): { merged: number[][]; skippedDuplicate: boolean } {
  const a = reverseA ? [...coordsA].reverse() : [...coordsA]
  let b = reverseB ? [...coordsB].reverse() : [...coordsB]

  const joinA = a[a.length - 1]
  const joinB = b[0]
  const skippedDuplicate =
    Math.abs(joinA[0] - joinB[0]) < 0.0001 && Math.abs(joinA[1] - joinB[1]) < 0.0001
  if (skippedDuplicate) {
    b = b.slice(1)
  }

  return { merged: [...a, ...b], skippedDuplicate }
}

/**
 * Compute the join endpoint for a given orientation.
 * Returns the two coordinates that would meet at the join point.
 */
export function getJoinEndpoints(
  coordsA: number[][],
  coordsB: number[][],
  reverseA: boolean,
  reverseB: boolean
): { endA: number[]; startB: number[] } {
  const endA = reverseA ? coordsA[0] : coordsA[coordsA.length - 1]
  const startB = reverseB ? coordsB[coordsB.length - 1] : coordsB[0]
  return { endA, startB }
}

/**
 * Join per-coordinate metadata arrays from two features, reversing as needed.
 * If only one feature has a given array, the other side is filled with nulls.
 */
export function joinMetadataArrays(
  propsA: Record<string, unknown>,
  propsB: Record<string, unknown>,
  reverseA: boolean,
  reverseB: boolean,
  skipDuplicate: boolean
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...propsA }
  for (const key of COORD_METADATA_KEYS) {
    const arrA = propsA[key]
    const arrB = propsB[key]
    if (!Array.isArray(arrA) && !Array.isArray(arrB)) continue

    let a: unknown[] = Array.isArray(arrA)
      ? [...arrA]
      : new Array((propsA as { _coordCount?: number })._coordCount ?? 0).fill(null)
    let b: unknown[] = Array.isArray(arrB)
      ? [...arrB]
      : new Array((propsB as { _coordCount?: number })._coordCount ?? 0).fill(null)

    if (reverseA) a = a.reverse()
    if (reverseB) b = b.reverse()
    if (skipDuplicate) b = b.slice(1)

    result[key] = [...a, ...b]
  }
  return result
}

// ---------------------------------------------------------------------------
// Label style computation (from base-shape.ts refreshLabel)
// ---------------------------------------------------------------------------

export interface LabelConfig {
  text?: string | undefined
  visible?: boolean | undefined
  fontColor?: string | undefined
  fontSize?: number | undefined
  fontFamily?: string | undefined
  horizontalAlign?: 'left' | 'center' | 'right' | undefined
  verticalAlign?: 'top' | 'middle' | 'bottom' | undefined
}

export interface LabelStyle {
  color: string
  textShadow: string
  textAlign: string
  padding: string
  anchorOffsetY: number
  fontSize: number
  fontFamily: string
}

/**
 * Compute the pure CSS/layout values needed to render a shape label.
 *
 * @param label         Label configuration from the feature
 * @param markerSize    Feature marker size in pixels (default 12)
 * @param strokeWidth   Feature stroke width in pixels (default 2)
 * @param containerW    Width of the label container div (default 300)
 */
export function computeLabelStyle(
  label: LabelConfig,
  markerSize = 12,
  strokeWidth = 2,
  containerW = 300
): LabelStyle {
  const color = label.fontColor ?? '#ffffff'
  const isLight = isLightColor(color)
  const shadowColor = isLight ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.9)'
  const textShadow = `1px 1px 2px ${shadowColor}, -1px -1px 2px ${shadowColor}, 1px -1px 2px ${shadowColor}, -1px 1px 2px ${shadowColor}`

  const hAlign = label.horizontalAlign ?? 'center'
  const vAlign = label.verticalAlign ?? 'bottom'
  const fontSize = label.fontSize ?? 11
  const fontFamily = label.fontFamily ?? 'sans-serif'

  const halfMarker = Math.round((markerSize + strokeWidth * 2) / 2)
  const gap = 8
  const textHeight = Math.round(fontSize * 1.3)

  // Horizontal: position text relative to shape
  // 'left' = text fully left of shape, 'right' = text fully right, 'center' = centered on shape
  let textAlign = 'center'
  let padding = '0'
  if (hAlign === 'left') {
    textAlign = 'right'
    padding = `0 ${containerW / 2 + halfMarker + gap}px 0 0`
  } else if (hAlign === 'right') {
    textAlign = 'left'
    padding = `0 0 0 ${containerW / 2 + halfMarker + gap}px`
  }

  // Vertical: position text relative to shape (positive Y = down on screen)
  // 'top' = text above shape, 'bottom' = text below shape, 'middle' = centered on shape
  let anchorOffsetY = 0
  if (vAlign === 'top') {
    anchorOffsetY = -(halfMarker + gap + textHeight)
  } else if (vAlign === 'middle') {
    anchorOffsetY = Math.round(textHeight / 2)
  } else {
    anchorOffsetY = halfMarker + gap
  }

  return { color, textShadow, textAlign, padding, anchorOffsetY, fontSize, fontFamily }
}
